import { useEffect, useMemo, useRef, useState } from "react";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import PaymentStrip from "../../payment/PaymentStrip";
import {
  clearMembershipApplyFormDraft,
  clearOnlinePaymentSession,
  clearPhonePePaymentResult,
  getMembershipApplyFormDraft,
  getPhonePePaymentResult,
  isActivePhonePePayment,
  markPhonePeOrderConsumed,
  setMembershipApplyFormDraft,
} from "../../payment/paymentSession";
import {
  getMembershipPlans,
  getStudentMembershipDetails,
  searchStudentsForMembership,
  submitMembershipApplication,
} from "../api/membershipVolunteerApi";
import { useMembershipRole } from "../utils/useMembershipRole";
import { MEMBERSHIPS_APPLY_SUBMODULE_SLUG } from "../utils/membershipPermissionSlugs";
import "../styles/tournamentRegistration.css";
import "../styles/studentSearch.css";
import { toLocalYmd } from "../../../public/utils/dateLocal";

const TRANSACTION_ID_MAX_LENGTH = 20;
const formatDateOnly = (date) => toLocalYmd(date);

const addDays = (startDate, daysToAdd) => {
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(daysToAdd || 0));
  return formatDateOnly(d);
};

const toMoneyNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const calcDiscountedSubscriptionFee = (price, discountPct) => {
  const safePrice = Math.max(0, toMoneyNumber(price));
  const safeDiscount = Math.min(100, Math.max(0, toMoneyNumber(discountPct)));
  return safePrice - (safePrice * safeDiscount) / 100;
};

const calcApplyAmount = (plan) => {
  if (!plan) return 0;
  const discountedSubscription = calcDiscountedSubscriptionFee(plan.price, plan.discount);
  const joiningFee = Math.max(0, toMoneyNumber(plan.registration_fee));
  return discountedSubscription + joiningFee;
};

const EMPTY_FORM = () => ({
  student_id: "",
  membership_id: "",
  payment_type: "offline",
  payment_transaction_id: "",
  payment_proof: "",
  payment_proof_file_name: "",
  amount: "",
  remarks: "",
});

export default function MembershipApplyPage() {
  const perms = useMembershipRole(MEMBERSHIPS_APPLY_SUBMODULE_SLUG);
  const [step, setStep] = useState(1);
  const [studentQuery, setStudentQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [student, setStudent] = useState(null);
  const [studentLocked, setStudentLocked] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [phonePeCompleted, setPhonePeCompleted] = useState(() => {
    const phonePe = getPhonePePaymentResult();
    return isActivePhonePePayment(phonePe) ? phonePe : null;
  });
  const [phonePeError, setPhonePeError] = useState("");
  const typeaheadRef = useRef(null);
  const timerRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  const [form, setForm] = useState(() => EMPTY_FORM());

  useEffect(() => {
    const onDoc = (e) => {
      if (!typeaheadRef.current?.contains(e.target)) setSuggestOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const plansRes = await getMembershipPlans();
        setPlans(plansRes.plans || []);
      } catch (e) {
        setError(e.message || "Failed to load apply-membership data");
      }
    })();
  }, []);

  useEffect(() => {
    const phonePe = getPhonePePaymentResult();
    const activePhonePe = isActivePhonePePayment(phonePe) ? phonePe : null;
    const draft = getMembershipApplyFormDraft();
    if (!draft && !activePhonePe) return;

    (async () => {
      if (draft?.student_id) {
        try {
          const details = await getStudentMembershipDetails(draft.student_id);
          setStudent(details.student || null);
          setStudentLocked(true);
          setStudentQuery(draft.studentQuery || details.student?.full_name || "");
        } catch {
          // ignore restore errors
        }
      }
      if (draft?.membership_id) {
        const plan = plans.find((p) => String(p.id) === String(draft.membership_id));
        setSelectedMembership(plan || null);
      }
      setForm((prev) => ({
        ...prev,
        ...(draft || {}),
        payment_type: activePhonePe || draft?.payment_type === "online" ? "online" : prev.payment_type,
      }));
      if (draft?.step) setStep(Number(draft.step) || 1);
      if (activePhonePe) setPhonePeCompleted(activePhonePe);
    })();
  }, [plans]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (studentLocked) return;
    if (!studentQuery.trim()) {
      setSuggestions([]);
      setSuggestOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      await handleStudentSearch({ q: studentQuery.trim() });
    }, 280);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [studentQuery, studentLocked]);

  const selectedPlan = useMemo(
    () => plans.find((p) => String(p.id) === String(form.membership_id)),
    [plans, form.membership_id],
  );
  const provisionalStartDate = useMemo(() => formatDateOnly(new Date()), []);
  const provisionalEndDate = useMemo(() => {
    const days = Number(selectedPlan?.duration_in_days || 0);
    if (!selectedPlan) return "";
    return days > 0 ? addDays(provisionalStartDate, days) : provisionalStartDate;
  }, [provisionalStartDate, selectedPlan]);
  const requiresPayment = useMemo(() => calcApplyAmount(selectedPlan) > 0, [selectedPlan]);
  const canSubmitAtCurrentStep = requiresPayment ? step >= 3 : step >= 2;
  const step1Done = !!student;
  const step2Done = !!selectedMembership;

  const handleStudentSearch = async ({ q = studentQuery.trim() } = {}) => {
    if (!q) {
      setSuggestions([]);
      setSuggestOpen(false);
      return;
    }
    setSearching(true);
    setError("");
    try {
      const result = await searchStudentsForMembership({ q, limit: 20 });
      const rows = result.students || [];
      setSuggestions(rows);
      setSuggestOpen(true);
      if (!rows.length) setError("No students found for this search");
    } catch (e) {
      setSuggestions([]);
      setSuggestOpen(false);
      setError(e.message || "Student search failed");
    } finally {
      setSearching(false);
    }
  };

  const onSelectStudent = async (s) => {
    try {
      const details = await getStudentMembershipDetails(s.id);
      setStudent(details.student || null);
      setForm((prev) => ({ ...prev, student_id: String(s.id) }));
      setStudentLocked(true);
      skipNextSearchRef.current = true;
      setStudentQuery(`${s.full_name || ""}${s.code ? ` (${s.code})` : ""}`.trim());
      setSuggestOpen(false);
      setSuggestions([]);
      setFieldErrors((prev) => ({ ...prev, student_id: undefined }));
      setStep(2);
    } catch (e) {
      setError(e.message || "Failed to load student details");
    }
  };

  const onStudentQueryChange = (value) => {
    setStudentQuery(value);
    if (studentLocked) {
      setStudentLocked(false);
      setStudent(null);
      setForm((prev) => ({
        ...prev,
        student_id: "",
        membership_id: "",
        amount: "",
      }));
      setSelectedMembership(null);
      setStep(1);
    }
  };

  const onField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError("");
    setFieldErrors((prev) => ({ ...prev, [k]: undefined }));
    if (k === "membership_id") {
      const plan = plans.find((p) => String(p.id) === String(v));
      const payableAmount = calcApplyAmount(plan);
      setSelectedMembership(plan || null);
      setForm((prev) => ({
        ...prev,
        membership_id: v,
        amount: plan ? String(payableAmount.toFixed(2)) : "",
      }));
      if (plan && payableAmount <= 0) {
        setStep((current) => (current > 2 ? 2 : current));
      }
    }
  };

  const clearForm = () => {
    setError("");
    setFieldErrors({});
    setPhonePeError("");
    setStudent(null);
    setStudentLocked(false);
    setStudentQuery("");
    setSuggestions([]);
    setSuggestOpen(false);
    setSelectedMembership(null);
    setStep(1);
    setPhonePeCompleted(null);
    clearOnlinePaymentSession();
    clearMembershipApplyFormDraft();
    setForm(EMPTY_FORM());
  };

  const validatePaymentStep = () => {
    const phonePe = getPhonePePaymentResult();
    const payingOnline =
      form.payment_type === "online" || isActivePhonePePayment(phonePe);
    if (!payingOnline) {
      const ref = String(form.payment_transaction_id || "").trim();
      if (!ref) return { payment_transaction_id: "Receipt number is required" };
      if (ref.length > TRANSACTION_ID_MAX_LENGTH) {
        return {
          payment_transaction_id: `Receipt number must be at most ${TRANSACTION_ID_MAX_LENGTH} characters`,
        };
      }
    } else if (!isActivePhonePePayment(phonePe)) {
      return { phonepe: "Complete online payment before submitting" };
    }
    if (!String(form.payment_proof || "").trim()) {
      return { proof: "Upload payment proof before submitting" };
    }
    return {};
  };

  const submit = async () => {
    if (!perms.canCreate) {
      setError("You do not have create access to submit applications.");
      return;
    }
    setError("");
    setSuccess("");
    setFieldErrors({});

    if (!student || !selectedPlan) {
      setError("Select a student and membership plan first.");
      return;
    }
    if (requiresPayment && step < 3) {
      setError("Complete each step in order before submitting.");
      return;
    }
    if (!requiresPayment && step < 2) {
      setError("Complete each step in order before submitting.");
      return;
    }

    if (requiresPayment) {
      const paymentErrors = validatePaymentStep();
      if (Object.keys(paymentErrors).length) {
        setFieldErrors(paymentErrors);
        setError("Please check the highlighted payment fields.");
        return;
      }
    }

    setSubmitting(true);
    try {
      let csrf = localStorage.getItem("csrf-token");
      if (!csrf) {
        csrf = await getCsrfToken();
        localStorage.setItem("csrf-token", csrf);
      }
      const phonePe = getPhonePePaymentResult();
      const paymentType =
        form.payment_type === "online" || isActivePhonePePayment(phonePe)
          ? "online"
          : "offline";
      const payload = {
        student_id: Number(form.student_id),
        membership_id: Number(form.membership_id),
        amount: requiresPayment ? Number(form.amount) : 0,
        remarks: form.remarks || null,
        payment: requiresPayment
          ? {
              payment_type: paymentType,
              transaction_id:
                paymentType === "offline"
                  ? form.payment_transaction_id?.trim()
                  : undefined,
              phonepe: paymentType === "online" ? phonePe : undefined,
              proof: form.payment_proof?.trim() || null,
              remarks: form.remarks || null,
            }
          : undefined,
      };
      const { data, error: apiError } = await submitMembershipApplication(payload, csrf);
      if (apiError) {
        setError(apiError.message || "Failed");
        if (apiError.details) setFieldErrors(apiError.details);
      } else if (data?.success) {
        if (phonePe?.orderId) markPhonePeOrderConsumed(phonePe.orderId);
        clearForm();
        setSuccess(
          typeof data?.message === "string" && data.message.trim()
            ? data.message.trim()
            : "Application submitted successfully",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="treg-page">
      <GlobalSuccessFlashModal message={success} onDismiss={() => setSuccess("")} />
      <header className="treg-hero">
        <p className="treg-hero__eyebrow">Memberships</p>
        <h2 className="treg-hero__title">Apply Membership</h2>
        <p className="treg-hero__sub">
          Search student, select membership, then pay offline or online via PhonePe. Free memberships skip the payment step.
        </p>
      </header>

      {error ? <GlobalFeedback type="error" text={error} /> : null}

      <div style={{ width: "100%" }}>
        <div className="treg-panel" style={{ width: "100%", maxWidth: "100%" }}>
          <h3 className="treg-panel__title" style={{ marginBottom: "1.5rem" }}>Membership Application</h3>
          <nav className="treg-stepper" aria-label="Application steps">
            {[
              { n: 1, label: "Student", done: step1Done },
              { n: 2, label: "Membership", done: step2Done },
              { n: 3, label: requiresPayment ? "Payment" : "Submit", done: false },
            ].map(({ n, label, done }) => (
              <div
                key={n}
                className={`treg-stepper__item${step === n ? " treg-stepper__item--active" : ""}${done && step !== n ? " treg-stepper__item--done" : ""}`}
              >
                <div className="treg-stepper__circle">{done && step !== n ? "✓" : n}</div>
                <span className="treg-stepper__label">{label}</span>
              </div>
            ))}
          </nav>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: "0.9rem",
              alignItems: "start",
            }}
          >
            <div className={`treg-step-section ${step === 1 ? "treg-step-section--open" : "treg-step-section--locked"}`} style={{ overflow: "visible" }}>
              <div className="treg-step-section__head">Search Student</div>
              <div className="treg-step-section__body">
                <div className="student-pay-student-typeahead" ref={typeaheadRef}>
                  <GlobalTextField
                    label="Search student"
                    value={studentQuery}
                    onChange={onStudentQueryChange}
                    placeholder="Type student id, mobile number, or name"
                    error={fieldErrors.student_id}
                  />
                  {suggestOpen && (searching || suggestions.length > 0) ? (
                    <div className="student-search-results student-pay-student-typeahead__dropdown">
                      {searching ? (
                        <div className="student-search-result-item">Searching...</div>
                      ) : (
                        suggestions.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className="student-search-result-item"
                            style={{ width: "100%", textAlign: "left", border: "none", background: "transparent" }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              onSelectStudent(s);
                            }}
                            onClick={() => onSelectStudent(s)}
                          >
                            <div className="student-search-result-info">
                              <strong>{s.full_name}</strong>
                              <span>{s.code || s.contact_number}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>

                {student ? (
                  <div className="treg-step-section treg-step-section--open" style={{ marginTop: "0.45rem" }}>
                    <div className="treg-step-section__head">Student Details Card</div>
                    <div className="treg-step-section__body" style={{ paddingTop: "0.5rem" }}>
                      <div className="treg-form-grid treg-form-grid--2">
                        <GlobalTextField label="Name" value={student.full_name || ""} onChange={() => {}} disabled />
                        <GlobalTextField label="Age" value={student.age != null ? String(student.age) : ""} onChange={() => {}} disabled />
                      </div>
                      <div className="treg-form-grid treg-form-grid--2">
                        <GlobalTextField label="Sport" value={student.sport_name || ""} onChange={() => {}} disabled />
                        <GlobalTextField label="Student Code" value={student.code || ""} onChange={() => {}} disabled />
                      </div>
                      <div className="treg-form-grid treg-form-grid--2">
                        <GlobalTextField label="Mobile Number" value={student.contact_number || ""} onChange={() => {}} disabled />
                        <GlobalTextField label="Email" value={student.email || ""} onChange={() => {}} disabled />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className={`treg-step-section ${step === 2 ? "treg-step-section--open" : "treg-step-section--locked"}`}>
              <div className="treg-step-section__head">Membership Selection</div>
              <div className="treg-step-section__body">
                <GlobalSelectField
                  label="Membership"
                  value={form.membership_id}
                  onChange={(v) => onField("membership_id", v)}
                  options={plans.map((p) => ({ value: p.id, label: `${p.membership_name} - ${p.duration_name || ""}` }))}
                  error={fieldErrors.membership_id}
                  disabled={!student}
                />
                <div className="treg-form-grid treg-form-grid--2">
                  <GlobalTextField label="Duration" value={selectedPlan?.duration_name || ""} onChange={() => {}} disabled />
                  <GlobalTextField
                    label="Duration (days)"
                    value={selectedPlan?.duration_in_days != null ? String(selectedPlan.duration_in_days) : ""}
                    onChange={() => {}}
                    disabled
                  />
                </div>
                <p style={{ margin: "0.25rem 0 0.4rem", fontSize: "0.85rem", color: "#475569" }}>
                  Start and end dates are set now as tentative and will be confirmed after payment verification.
                </p>
                <div className="treg-form-grid treg-form-grid--2">
                  <GlobalTextField label="Provisional start date" value={selectedPlan ? provisionalStartDate : ""} onChange={() => {}} disabled />
                  <GlobalTextField label="Provisional end date" value={selectedPlan ? provisionalEndDate : ""} onChange={() => {}} disabled />
                </div>
                <GlobalTextField label="Amount" type="number" value={form.amount} onChange={() => {}} error={fieldErrors.amount} disabled />
                {!requiresPayment ? (
                  <GlobalTextField label="Remarks" value={form.remarks} onChange={(v) => onField("remarks", v)} />
                ) : null}
                <div className="treg-btn-row treg-actions--between">
                  <GlobalButton type="button" variant="ghost" onClick={() => setStep(1)}>
                    Back
                  </GlobalButton>
                  <GlobalButton
                    type="button"
                    variant="primary"
                    disabled={
                      !selectedMembership ||
                      (!requiresPayment && (!perms.canCreate || submitting))
                    }
                    onClick={() => {
                      if (requiresPayment) setStep(3);
                      else submit();
                    }}
                  >
                    {requiresPayment ? "Continue to payment" : submitting ? "Submitting..." : "Submit application"}
                  </GlobalButton>
                </div>
              </div>
            </div>

            {requiresPayment ? (
              <div className={`treg-step-section ${step === 3 ? "treg-step-section--open" : "treg-step-section--locked"}`}>
                <div className="treg-step-section__head">Payment Section</div>
                <div className="treg-step-section__body">
                  <PaymentStrip
                    paymentType={
                      isActivePhonePePayment(phonePeCompleted) ? "online" : form.payment_type
                    }
                    onBeforePayRedirect={() => {
                      clearPhonePePaymentResult();
                      setPhonePeCompleted(null);
                      setMembershipApplyFormDraft({
                        ...form,
                        payment_type: "online",
                        studentQuery,
                        step: 3,
                      });
                    }}
                    onClearOnlinePayment={() => {
                      clearOnlinePaymentSession();
                      clearMembershipApplyFormDraft();
                      setPhonePeCompleted(null);
                      setPhonePeError("");
                      setFieldErrors((prev) => ({ ...prev, phonepe: "" }));
                    }}
                    onPaymentTypeChange={(type) => {
                      onField("payment_type", type);
                      setPhonePeError("");
                      if (type === "online") {
                        onField("payment_transaction_id", "");
                        if (fieldErrors.payment_transaction_id) {
                          setFieldErrors((prev) => ({ ...prev, payment_transaction_id: "" }));
                        }
                      } else if (fieldErrors.phonepe) {
                        setFieldErrors((prev) => ({ ...prev, phonepe: "" }));
                      }
                    }}
                    receiptValue={form.payment_transaction_id}
                    onReceiptChange={(v) =>
                      onField(
                        "payment_transaction_id",
                        String(v ?? "").slice(0, TRANSACTION_ID_MAX_LENGTH),
                      )
                    }
                    receiptError={fieldErrors.payment_transaction_id}
                    amount={form.amount}
                    phonePeCompleted={phonePeCompleted}
                    phonePeErrorMsg={phonePeError}
                    onPhonePeError={setPhonePeError}
                    formErrorDetails={fieldErrors}
                    formSubmitting={submitting}
                    proofUrl={form.payment_proof}
                    proofFileName={form.payment_proof_file_name}
                    proofError={fieldErrors.proof}
                    onProofChange={({ url, fileName }) => {
                      onField("payment_proof", url || "");
                      onField("payment_proof_file_name", fileName || "");
                      if (fieldErrors.proof) {
                        setFieldErrors((prev) => ({ ...prev, proof: "" }));
                      }
                    }}
                  />
                  <GlobalTextField
                    label="Remarks"
                    value={form.remarks}
                    onChange={(v) => onField("remarks", v)}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="treg-actions" style={{ marginTop: "1rem" }}>
            <GlobalButton type="button" variant="danger" onClick={clearForm} disabled={submitting}>
              Clear form
            </GlobalButton>
            {requiresPayment ? (
              <GlobalButton type="button" variant="ghost" onClick={() => setStep(2)} disabled={step !== 3}>
                Back
              </GlobalButton>
            ) : null}
            {requiresPayment ? (
              <GlobalButton
                type="button"
                variant="primary"
                disabled={
                  submitting ||
                  !perms.canCreate ||
                  !student ||
                  !selectedPlan ||
                  !canSubmitAtCurrentStep
                }
                onClick={() => submit()}
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </GlobalButton>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
